/**
 * Lokal schreiben und in die Outbox stellen — **in einer Transaktion**.
 *
 * Beides zusammen oder gar nicht. Ginge die Zeile durch und der Outbox-Eintrag
 * nicht, wäre die Änderung auf dem Gerät sichtbar und käme nie beim Server an:
 * stiller Datenverlust, und der ist das einzige wirklich schlimme Verhalten.
 *
 * Bis E3 stand dieser Ablauf in jeder Schreibfunktion noch einmal. Mit dem
 * Training kämen zwei Dutzend weitere Stellen dazu — jede davon eine
 * Gelegenheit, die Transaktion zu vergessen.
 */

import { newId } from '@kraftwerk/shared';
import type { MutationKind, SyncTable, SyncedRow } from '@kraftwerk/shared';
import { db } from './local.js';
import type { KraftwerkDatabase } from './local.js';

export interface RowChange<T extends SyncedRow = SyncedRow> {
  table: SyncTable;
  row: T;
  kind?: MutationKind;
}

/**
 * Mehrere Zeilen als **eine** Transaktion schreiben.
 *
 * Nötig, sobald zusammengehörige Zeilen entstehen: eine Einheit und ihr erster
 * Satz, ein Trainingstag und seine Übungen. Halb angekommen wäre schlimmer als
 * gar nicht.
 */
export async function writeRows(
  changes: readonly RowChange[],
  database: KraftwerkDatabase = db,
): Promise<void> {
  if (changes.length === 0) return;

  const tables = [...new Set(changes.map((change) => database.tableOf(change.table)))];
  const now = new Date().toISOString();

  await database.transaction('rw', [...tables, database.outbox], async () => {
    for (const change of changes) {
      const kind = change.kind ?? 'upsert';
      await database.tableOf(change.table).put(change.row as never);
      await database.outbox.add({
        mutationId: newId(),
        table: change.table,
        kind,
        row: change.row,
        createdAt: now,
        attempts: 0,
      });
    }
  });
}

export async function writeRow<T extends SyncedRow>(
  table: SyncTable,
  row: T,
  kind: MutationKind = 'upsert',
  database: KraftwerkDatabase = db,
): Promise<T> {
  await writeRows([{ table, row, kind }], database);
  return row;
}

/** Zeitstempel für eine Änderung — an einer Stelle, damit alle Zeilen dieselbe Form haben. */
export function stamp(): string {
  return new Date().toISOString();
}
